<template>
  <Card
    :id="lineItem.domId ?? `line-item-num-${index + 1}`"
    class="line-item-view-edit"
    :class="{ 'edit-mode': isInEditMode }"
    :aria-expanded="isInEditMode ? 'true' : 'false'"
    :aria-invalid="!!errorResponse ? 'true' : 'false'"
    tabindex="-1"
  >
    <template #content>
      <LineItemEditGrid
        v-if="isInEditMode"
        v-model="lineItem"
        :index="index"
        :handling-units="handlingUnits"
        :error-response="errorResponse"
        :removable="editable && removable"
        :show-stops="showStops"
        :requires-confirmation="requiresConfirmation"
        class="w-full"
        @cancel="onCancel"
        @done="onDone"
        @remove="emit('remove', $event)"
      />
      <LineItemView
        v-else
        :line-item="lineItem"
        :removable="editable && removable"
        :editable="editable"
        :show-stops="showStops"
        @duplicate="emit('duplicate', $event)"
        @edit="isInEditMode = true"
        @remove="emit('remove', $event)"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { ref, toRefs, watch } from 'vue';

import { useValidations } from '@rygen/rygen-ui/composables';
import { ErrorResponse, HandlingUnit, Loadable } from '@rygen/rygen-ui/model';

import { LineItem } from '@/model';

import LineItemEditGrid from '@/components/lineItem/LineItemEditGrid.vue';
import LineItemView from '@/components/lineItem/LineItemView.vue';
import Card from 'primevue/card';

const isInEditMode = defineModel('editMode', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: LineItem,
    default: () => new LineItem(),
  },
  index: {
    type: Number,
    default: () => 1,
  },
  handlingUnits: {
    type: Object as PropType<Loadable<HandlingUnit[]>>,
    default: () => ({ loading: false }),
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  /**
   * Determines if `update:model-value` is only sent on user confirmation
   *
   * When this prop is true, we render confirmation buttons and short-circuit
   * two-way binding. `update:model-value` will **not** be emitted on changes.
   * It will only be emitted when the _Done_ button is pressed.
   */
  requiresConfirmation: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  index,
  handlingUnits,
  errorResponse,
  removable,
  editable,
  requiresConfirmation,
  showStops,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel', lineItem: LineItem): void;
  (event: 'change', isChanged: boolean): void;
  (event: 'done', lineItem: LineItem): void;
  (event: 'duplicate', lineItem: LineItem): void;
  (event: 'remove', lineItem: LineItem): void;
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const Clazz = LineItem.getSubclass(modelValue.value);
const lineItem: Ref<LineItem> = ref(new Clazz(modelValue.value));
const originalState: LineItem = new Clazz(modelValue.value);

useValidations(lineItem, {});

const onDone = () => {
  isInEditMode.value = false;
  emit('done', lineItem.value);
};

const onCancel = () => {
  isInEditMode.value = false;
  emit('cancel', originalState);
};

watch(
  modelValue,
  (newVal: LineItem) => {
    lineItem.value = new Clazz(newVal);
  },
  { immediate: true, deep: true },
);

watch(
  lineItem,
  (newVal: LineItem) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
    emit('change', !isEqual(newVal, originalState));
  },
  { deep: true },
);
</script>

<style>
.line-item-view-edit.p-card {
  --p-card-border-radius: 0 var(--p-border-radius-sm) var(--p-border-radius-sm)
    var(--p-border-radius-sm);
  --line-item-view-edit-card-body-padding: 0;
  --line-item-view-edit-border-color: var(--p-blue-500);

  counter-increment: line-item-num;
  border-left: var(--sp-1) solid var(--line-item-view-edit-border-color);
  position: relative;
  z-index: 2;

  &.edit-mode {
    --line-item-view-edit-card-body-padding: var(--sp-3);
  }

  .p-card-body {
    padding: var(--line-item-view-edit-card-body-padding);
  }

  &::before {
    content: counter(line-item-num);
    position: absolute;
    left: calc(var(--sp-6) * -1);
    background-color: var(--line-item-view-edit-border-color);
    color: white;
    padding: var(--sp-2);
    border-radius: var(--p-border-radius-sm) 0 0 var(--p-border-radius-sm);
    font-weight: 700;
  }

  &[aria-invalid='true'][aria-expanded='false'] {
    --line-item-view-edit-border-color: var(--danger-color);
  }
}
</style>
